import ListPageController from "./mixins/ListPageController";
import Fragment from "sap/ui/core/Fragment";
import JSONModel from "sap/ui/model/json/JSONModel";
import MessageBox from "sap/m/MessageBox";
import MessageToast from "sap/m/MessageToast";
import type Dialog from "sap/m/Dialog";
import type MTable from "sap/m/Table";
import type ColumnListItem from "sap/m/ColumnListItem";
import type Context from "sap/ui/model/odata/v4/Context";
import type ODataListBinding from "sap/ui/model/odata/v4/ODataListBinding";
import type { SearchField$LiveChangeEvent, SearchField$SearchEvent } from "sap/m/SearchField";
import type { Select$ChangeEvent } from "sap/m/Select";
import type { Link$PressEvent } from "sap/m/Link";
import type { ListBase$SelectionChangeEvent } from "sap/m/ListBase";
import { getCountries, type CountryItem } from "../model/countries";
import type { ResponseError } from "../types/Errors";
import type ResourceBundle from "sap/base/i18n/ResourceBundle";
import { problemCode, problemMessage } from "../util/ProblemDetails";
type Scope = "all" | "partners" | "customers";
type OrgType = "Partner" | "EndCustomer";
type IssuerMode = "CompuTec" | "Partner";
type TypeKey = "ALL" | "Partner" | "EndCustomer" | "CompuTec";
type StatusKey = "ALL" | "Active" | "PendingAcceptance" | "Suspended" | "Rejected";

interface VariantOption {
	key: string;
	text: string;
}

interface OrgDraft {
	name: string; taxId: string; country: string; city: string;
	region: string; postalCode: string; email: string;
	contractDate: string; contractExpiration: string;
	customerStatus: string; cardCode: string;
}

interface AddOrgState {
	type: OrgType;
	title: string;
	parentDisplay: string;
	parentOrgId: string;
	issuerMode: IssuerMode;
	issuerOrgId: string;
	issuerPath: string;
	issuerDisplay: string;
	cardCodeValueState: "None" | "Error";
	nameValueState: "None" | "Error";
	nameValueStateText: string;
	countries: CountryItem[];
	draft: OrgDraft;
}

interface IssuerPickerState {
	searchQuery: string;
	selectedId: string;
	selectedName: string;
	selectedPath: string;
}

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const CARD_CODE_PATTERN = /^[A-Za-z0-9]{1,50}$/;

const EMPTY_DRAFT: OrgDraft = {
	name: "", taxId: "", country: "", city: "",
	region: "", postalCode: "", email: "",
	contractDate: "", contractExpiration: "",
	customerStatus: "", cardCode: ""
};

interface ViewState {
	scope: Scope;
	/** A partner's Customers list: all of its child organizations, sub-Partners included (spec 29 D-131). */
	childScope: boolean;
	typeFilter: TypeKey;
	statusFilter: StatusKey;
	searchQuery: string;
	variantKey: string;
	availableVariants: VariantOption[];
}

interface ScopeConfig {
	titleKey: string;
	subtitleKey: string;
	totalSubtitleKey: string;
	defaultType: TypeKey;
}

const SCOPE_CONFIG: Record<Scope, ScopeConfig> = {
	all: { titleKey: "organizations.title", subtitleKey: "organizations.subtitle", totalSubtitleKey: "organizations.stats.total.subtitle", defaultType: "ALL" },
	partners: { titleKey: "partners.title", subtitleKey: "partners.subtitle", totalSubtitleKey: "partners.stats.total.subtitle", defaultType: "Partner" },
	customers: { titleKey: "customers.title", subtitleKey: "customers.subtitle", totalSubtitleKey: "customers.stats.total.subtitle", defaultType: "EndCustomer" }
};

interface VariantPreset {
	type: TypeKey;
	status: StatusKey;
	search: string;
}

// Predefiniowane warianty filtrów — gotowe "ulubione" zestawy. User może je nadpisywać
// ad-hoc zmieniając Type/Status/Search, ale nie zapisuje swoich (na razie).
const VARIANTS: Record<string, VariantPreset> = {
	"standard":         { type: "ALL",         status: "ALL",               search: "" },
	"partners-active":  { type: "Partner",     status: "Active",            search: "" },
	"customers-active": { type: "EndCustomer", status: "Active",            search: "" },
	"pending":          { type: "ALL",         status: "PendingAcceptance", search: "" },
	"suspended":        { type: "ALL",         status: "Suspended",         search: "" }
};

// Variants per scope: CompuTec's /customers forces Type to EndCustomer, so "Active partners" makes no
// sense there. A partner's /customers (childScope) holds both types and gets the "all" list.
const VARIANTS_PER_SCOPE: Record<Scope, string[]> = {
	all:       ["standard", "partners-active", "customers-active", "pending", "suspended"],
	partners:  ["standard", "partners-active",                       "pending", "suspended"],
	customers: ["standard",                    "customers-active",   "pending", "suspended"]
};

/**
 * @namespace computec.b2b.portal.controller
 */
export default class Organizations extends ListPageController {
	private addOrgDialog?: Dialog;
	private addOrgIssuerPickerDialog?: Dialog;
	private computecOrgId?: string;
	/** initStats and applyScope both count; only the latest request may write the tiles. */
	private statsRequest = 0;

	public override onInit(): void {
		const initial: ViewState = {
			scope: "all",
			childScope: false,
			typeFilter: "ALL",
			statusFilter: "ALL",
			searchQuery: "",
				variantKey: "standard",
			availableVariants: []
		};
		this.getView().setModel(new JSONModel(initial), "view");
		this.getView().setModel(new JSONModel({ cards: [] }), "stats");

		void this.initStats();

		// Same view serves three routes — scope determines page header, default type filter,
		// and which "Add" actions are visible.
		const router = this.getRouter();
		router.getRoute("organizations")?.attachPatternMatched(() => { void this.applyScope("all"); });
		router.getRoute("partners")?.attachPatternMatched(() => { void this.applyScope("partners"); });
		router.getRoute("customers")?.attachPatternMatched(() => { void this.applyScope("customers"); });
	}

	private async applyScope(scope: Scope): Promise<void> {
		const bundle = await this.getResourceBundle();
		const cfg = SCOPE_CONFIG[scope];
		const model = this.getView().getModel("view") as JSONModel;
		const childScope = scope === "customers" && this.isPartnerUser();
		model.setProperty("/scope", scope);
		model.setProperty("/childScope", childScope);
		this.applyTotalSubtitle(bundle);
		model.setProperty("/typeFilter", childScope ? "ALL" : cfg.defaultType);
		model.setProperty("/statusFilter", "ALL");
		model.setProperty("/searchQuery", "");
		model.setProperty("/variantKey", "standard");

		// Variant Select dostaje listę dopasowaną do scope — na /customers brak "partners-active".
		const variantKeys = VARIANTS_PER_SCOPE[childScope ? "all" : scope];
		const variantOptions: VariantOption[] = variantKeys.map(key => ({
			key,
			text: bundle.getText(`organizations.variant.${variantKeyToI18n(key)}`) ?? key
		}));
		model.setProperty("/availableVariants", variantOptions);

		this.setPageHeader({
			title: bundle.getText(cfg.titleKey) ?? "Organizations",
			subtitle: bundle.getText(cfg.subtitleKey) ?? ""
		});

		this.applyFilters();
		await this.refreshStats();
	}

	private async initStats(): Promise<void> {
		const bundle = await this.getResourceBundle();
		// Ten sam format co KPI cards na Dashboardzie (NumericHeader):
		// state to sap.m.ValueColor (None/Good/Error/Critical/Neutral), value zawsze string.
		const cards = [
			{ key: "total", label: bundle.getText("organizations.stats.total.label") ?? "Total", subtitle: bundle.getText("organizations.stats.total.subtitle") ?? "", value: "—", state: "Neutral" },
			{ key: "active", label: bundle.getText("organizations.stats.active.label") ?? "Active", subtitle: bundle.getText("organizations.stats.active.subtitle") ?? "", value: "—", state: "Good" },
			{ key: "pending", label: bundle.getText("organizations.stats.pending.label") ?? "Pending", subtitle: bundle.getText("organizations.stats.pending.subtitle") ?? "", value: "—", state: "Critical" },
			{ key: "suspended", label: bundle.getText("organizations.stats.suspended.label") ?? "Suspended", subtitle: bundle.getText("organizations.stats.suspended.subtitle") ?? "", value: "—", state: "Error" }
		];
		const model = this.getView().getModel("stats") as JSONModel;
		model.setProperty("/cards", cards);
		this.applyTotalSubtitle(bundle);
		await this.refreshStats();
	}

	/**
	 * The Total tile names the current scope. Called from both initStats and applyScope, because
	 * either may finish first when the page is opened directly on /partners or /customers.
	 */
	private applyTotalSubtitle(bundle: ResourceBundle): void {
		const stats = this.getView().getModel("stats") as JSONModel | undefined;
		if (!stats?.getProperty("/cards/0")) {
			return;
		}
		const scope = ((this.getView().getModel("view") as JSONModel).getProperty("/scope") as Scope) ?? "all";
		stats.setProperty("/cards/0/subtitle", bundle.getText(SCOPE_CONFIG[scope].totalSubtitleKey) ?? "");
	}

	private async refreshStats(): Promise<void> {
		const request = ++this.statsRequest;
		const view = this.getView().getModel("view") as JSONModel;
		const scope = (view.getProperty("/scope") as Scope) ?? "all";
		const cfg = SCOPE_CONFIG[scope];
		const typeClause = view.getProperty("/childScope") === true
			? this.ownOrgExclusion()
			: cfg.defaultType === "ALL" ? "" : `Type eq '${cfg.defaultType}'`;
		const join = (a: string, b: string): string => (a && b) ? `${a} and ${b}` : (a || b);

		const filters = [
			typeClause,
			join(typeClause, "Status eq 'Active'"),
			join(typeClause, "Status eq 'PendingAcceptance'"),
			join(typeClause, "Status eq 'Suspended'")
		];
		const model = this.getView().getModel("stats") as JSONModel | undefined;
		if (!model) {
			return;
		}
		const counts = await Promise.all(filters.map(f => this.fetchCount(f)));
		if (request !== this.statsRequest) {
			return;
		}
		counts.forEach((value, idx) => {
			if (value !== null) {
				// NumericHeader.number jest typu string — raw Number potrafi wywalić strict validator.
				model.setProperty(`/cards/${idx}/value`, String(value));
			}
		});
	}

	private async fetchCount(filter: string): Promise<number | null> {
		const fetcher = this.getOwnerComponent().getAuthFetch();
		try {
			const filterPart = filter ? `?$filter=${encodeURIComponent(filter)}` : "";
			const r = await fetcher.fetch(`/odata/v4/Organizations/$count${filterPart}`, {
				headers: { Accept: "text/plain" }
			});
			if (!r.ok) {
				return null;
			}
			const n = Number((await r.text()).trim());
			return Number.isFinite(n) ? n : null;
		} catch {
			return null;
		}
	}

	// === Filters / search / variants ===

	public onSearch(event: SearchField$SearchEvent | SearchField$LiveChangeEvent): void {
		const params = event.getParameters() as { query?: string; newValue?: string };
		const raw = params.query ?? params.newValue ?? "";
		(this.getView().getModel("view") as JSONModel).setProperty("/searchQuery", raw);
		this.applyFilters();
	}

	public onTypeSelect(event: Select$ChangeEvent): void {
		const key = (event.getParameter("selectedItem")?.getKey() ?? "ALL") as TypeKey;
		(this.getView().getModel("view") as JSONModel).setProperty("/typeFilter", key);
		this.applyFilters();
	}

	public onStatusSelect(event: Select$ChangeEvent): void {
		const key = (event.getParameter("selectedItem")?.getKey() ?? "ALL") as StatusKey;
		(this.getView().getModel("view") as JSONModel).setProperty("/statusFilter", key);
		this.applyFilters();
	}

	public onVariantSelect(event: Select$ChangeEvent): void {
		const key = event.getParameter("selectedItem")?.getKey() ?? "standard";
		const preset = VARIANTS[key];
		if (!preset) {
			return;
		}
		const model = this.getView().getModel("view") as JSONModel;
		const scope = model.getProperty("/scope") as Scope;
		const cfg = SCOPE_CONFIG[scope];
		// /partners and CompuTec's /customers force their default Type, so "Standard" there does not show
		// other types; the "all" scope and a partner's child list take the variant's own Type.
		const effectiveType = scope === "all" || model.getProperty("/childScope") === true ? preset.type : cfg.defaultType;
		model.setProperty("/variantKey", key);
		model.setProperty("/typeFilter", effectiveType);
		model.setProperty("/statusFilter", preset.status);
		model.setProperty("/searchQuery", preset.search);
		this.applyFilters();
	}

	public onRefresh(): void {
		this.refreshTable("orgTable");
	}

	private applyFilters(): void {
		const state = (this.getView().getModel("view") as JSONModel).getData() as ViewState;
		const parts: string[] = [];

		// Raw $filter string omija UI5 ODataListBinding._Helper.formatLiteral, który nie umie
		// formatować literałów dla custom .NET enum types (PortalB2B.Domain.Organizations.OrganizationType etc.).
		// tolower(Name) — case-insensitive search po nazwie. TaxId (NIP) dołączony przez guard
		// `TaxId ne null` PRZED contains — na nullowym stringu goły contains/tolower wywala backend 500,
		// więc zawężamy do wierszy z uzupełnionym NIP (NIP to cyfry — bez tolower). Path pominięty
		// (custom value object / ltree).
		const search = state.searchQuery.trim();
		if (search.length > 0) {
			const qLower = escapeODataString(search.toLowerCase());
			const qRaw = escapeODataString(search);
			parts.push(`(contains(tolower(Name),'${qLower}') or (TaxId ne null and contains(TaxId,'${qRaw}')))`);
		}
		if (state.childScope) {
			const exclusion = this.ownOrgExclusion();
			if (exclusion) {
				parts.push(exclusion);
			}
		}
		if (state.typeFilter !== "ALL") {
			parts.push(`Type eq '${state.typeFilter}'`);
		}
		if (state.statusFilter !== "ALL") {
			parts.push(`Status eq '${state.statusFilter}'`);
		}

		const binding = this.getTableBinding("orgTable");
		binding?.changeParameters({ $filter: parts.length > 0 ? parts.join(" and ") : undefined });
	}

	public onOpenOrganization(event: Link$PressEvent): void {
		const oCtx = event.getSource().getBindingContext("odata");
		if (!oCtx) {
			return;
		}
		const id = oCtx.getProperty("Id") as string | undefined;
		if (!id) {
			return;
		}
		const scope = (this.getView().getModel("view") as JSONModel).getProperty("/scope") as string;
		const route = scope === "partners" ? "partnerDetail" : scope === "customers" ? "customerDetail" : "organizationDetail";
		this.navTo(route, { id });
	}

	// === Add Partner / Add Customer — dialog ===

	public onAddPartnerPress(): void {
		void this.openAddOrgDialog("Partner");
	}

	public onAddCustomerPress(): void {
		void this.openAddOrgDialog("EndCustomer");
	}

	private async openAddOrgDialog(type: OrgType): Promise<void> {
		const bundle = await this.getResourceBundle();
		const state: AddOrgState = {
			type,
			title: bundle.getText(type === "Partner" ? "organizations.new.partner.title" : "organizations.new.customer.title") ?? "",
			parentDisplay: "",
			parentOrgId: "",
			issuerMode: "CompuTec",
			issuerOrgId: "",
			issuerPath: "",
			issuerDisplay: "",
			cardCodeValueState: "None",
			nameValueState: "None",
			nameValueStateText: "",
			countries: getCountries(),
			draft: { ...EMPTY_DRAFT }
		};
		this.getView().setModel(new JSONModel(state), "addOrg");
		this.getView().setModel(new JSONModel({ searchQuery: "", selectedId: "", selectedName: "", selectedPath: "" } satisfies IssuerPickerState), "issuerPicker");

		if (!this.addOrgDialog) {
			this.addOrgDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.AddOrganizationDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.addOrgDialog);
		}

		await this.resolveAddOrgParent();
		this.addOrgDialog.open();
	}

	private async resolveAddOrgParent(): Promise<void> {
		const model = this.getView().getModel("addOrg") as JSONModel;
		const state = model.getData() as AddOrgState;
		const isCompuTec = this.hasIssuerChoice();

		// A partner's new organization, sub-Partner or customer, goes under the partner itself (D-131);
		// CompuTec's new partners go under CompuTec.
		if (isCompuTec || (state.type === "Partner" && !this.isPartnerUser())) {
			const id = await this.ensureComputecOrgId();
			model.setProperty("/parentOrgId", id ?? "");
			model.setProperty("/parentDisplay", id ? "CompuTec" : "");
			return;
		}

		const userModel = this.getOwnerComponent().getModel("user") as JSONModel | undefined;
		const ownOrgId = (userModel?.getProperty("/orgId") as string | undefined) ?? "";
		const ownOrgName = (userModel?.getProperty("/orgName") as string | undefined) ?? "";
		model.setProperty("/parentOrgId", ownOrgId);
		model.setProperty("/parentDisplay", ownOrgName.length > 0 ? ownOrgName : "—");
	}

	private isPartnerUser(): boolean {
		return this.getOwnerComponent().getPermissions()?.organizationType() === "Partner";
	}

	/** Keeps the partner's own organization off its child list; empty when the id is not known yet. */
	private ownOrgExclusion(): string {
		const userModel = this.getOwnerComponent().getModel("user") as JSONModel | undefined;
		const ownOrgId = (userModel?.getProperty("/orgId") as string | undefined) ?? "";
		return UUID_RE.test(ownOrgId) ? `Id ne ${ownOrgId}` : "";
	}

	private hasIssuerChoice(): boolean {
		return this.getOwnerComponent().getPermissions().has("orgs.read-all");
	}

	private async ensureComputecOrgId(): Promise<string | undefined> {
		if (this.computecOrgId) {
			return this.computecOrgId;
		}
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const r = await fetcher.fetch(
				"/odata/v4/Organizations?$filter=Type%20eq%20'CompuTec'&$select=Id&$top=1",
				{ headers: { Accept: "application/json" } }
			);
			if (!r.ok) { return undefined; }
			const data = await r.json() as { value?: { Id: string }[] };
			this.computecOrgId = data.value?.[0]?.Id;
			return this.computecOrgId;
		} catch {
			return undefined;
		}
	}

	public onCancelAddOrg(): void {
		this.addOrgDialog?.close();
	}

	public onAddOrgCardCodeLiveChange(): void {
		const model = this.getView().getModel("addOrg") as JSONModel;
		const value = (model.getProperty("/draft/cardCode") as string).trim();
		const ok = value.length === 0 || CARD_CODE_PATTERN.test(value);
		model.setProperty("/cardCodeValueState", ok ? "None" : "Error");
	}

	public onAddOrgIssuerModeChange(): void {
		const model = this.getView().getModel("addOrg") as JSONModel;
		model.setProperty("/issuerOrgId", "");
		model.setProperty("/issuerPath", "");
		model.setProperty("/issuerDisplay", "");
		const mode = model.getProperty("/issuerMode") as IssuerMode;
		if (mode === "CompuTec") {
			model.setProperty("/parentOrgId", this.computecOrgId ?? "");
			model.setProperty("/parentDisplay", this.computecOrgId ? "CompuTec" : "");
		} else {
			model.setProperty("/parentOrgId", "");
			model.setProperty("/parentDisplay", "");
		}
	}

	public async onAddOrgIssuerValueHelp(): Promise<void> {
		if (!this.addOrgIssuerPickerDialog) {
			this.addOrgIssuerPickerDialog = await Fragment.load({
				id: this.getView().getId() + "-issuer",
				name: "computec.b2b.portal.view.IssuerPickerDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.addOrgIssuerPickerDialog);
			this.addOrgIssuerPickerDialog.attachAfterOpen(() => this.applyAddOrgIssuerFilters());
		}
		const state = (this.getView().getModel("addOrg") as JSONModel).getData() as AddOrgState;
		const picker = this.getView().getModel("issuerPicker") as JSONModel;
		picker.setProperty("/searchQuery", "");
		picker.setProperty("/selectedId", state.issuerOrgId);
		picker.setProperty("/selectedName", state.issuerDisplay);
		picker.setProperty("/selectedPath", state.issuerPath);
		this.applyAddOrgIssuerFilters();
		this.addOrgIssuerPickerDialog.open();
	}

	public onIssuerPickerSearch(event: SearchField$SearchEvent | SearchField$LiveChangeEvent): void {
		const params = event.getParameters() as { query?: string; newValue?: string };
		const raw = params.query ?? params.newValue ?? "";
		(this.getView().getModel("issuerPicker") as JSONModel).setProperty("/searchQuery", raw);
		this.applyAddOrgIssuerFilters();
	}

	public onIssuerPickerSelectionChange(event: ListBase$SelectionChangeEvent): void {
		const item = event.getParameter("listItem") as ColumnListItem | undefined;
		const ctx = item?.getBindingContext("odata") as Context | null | undefined;
		const picker = this.getView().getModel("issuerPicker") as JSONModel;
		if (!ctx) {
			picker.setProperty("/selectedId", "");
			picker.setProperty("/selectedName", "");
			picker.setProperty("/selectedPath", "");
			return;
		}
		picker.setProperty("/selectedId", String(ctx.getProperty("Id") ?? ""));
		picker.setProperty("/selectedName", String(ctx.getProperty("Name") ?? ""));
		picker.setProperty("/selectedPath", String(ctx.getProperty("Path") ?? ""));
	}

	public onIssuerPickerConfirm(): void {
		const picker = (this.getView().getModel("issuerPicker") as JSONModel).getData() as IssuerPickerState;
		if (!UUID_RE.test(picker.selectedId)) { return; }
		const model = this.getView().getModel("addOrg") as JSONModel;
		model.setProperty("/issuerOrgId", picker.selectedId);
		model.setProperty("/issuerPath", picker.selectedPath);
		model.setProperty("/issuerDisplay", picker.selectedName);
		model.setProperty("/parentOrgId", picker.selectedId);
		model.setProperty("/parentDisplay", picker.selectedName);
		this.addOrgIssuerPickerDialog?.close();
	}

	public onIssuerPickerCancel(): void {
		this.addOrgIssuerPickerDialog?.close();
	}

	private applyAddOrgIssuerFilters(): void {
		const table = this.byId("issuer-issuerPickerTable") as MTable | undefined
			?? this.byId("issuerPickerTable") as MTable | undefined;
		const binding = table?.getBinding("items") as ODataListBinding | undefined;
		if (!binding) { return; }
		const state = (this.getView().getModel("issuerPicker") as JSONModel).getData() as IssuerPickerState;
		const parts: string[] = ["Type eq 'Partner'"];
		if (state.searchQuery.trim().length > 0) {
			const q = state.searchQuery.trim().toLowerCase().replace(/'/g, "''");
			parts.push(`contains(tolower(Name),'${q}')`);
		}
		binding.changeParameters({ $filter: parts.join(" and ") });
	}

	/** Czyści błąd walidacji nazwy przy edycji pola (np. po odrzuceniu duplikatu przez backend). */
	public onAddOrgNameChange(): void {
		const model = this.getView().getModel("addOrg") as JSONModel;
		if (model.getProperty("/nameValueState") !== "None") {
			model.setProperty("/nameValueState", "None");
			model.setProperty("/nameValueStateText", "");
		}
	}

	public async onSaveAddOrg(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const model = this.getView().getModel("addOrg") as JSONModel;
		const state = model.getData() as AddOrgState;
		const draft = state.draft;

		model.setProperty("/nameValueState", "None");
		model.setProperty("/nameValueStateText", "");

		const errors: string[] = [];
		if (this.hasIssuerChoice() && state.type === "EndCustomer" && state.issuerMode === "Partner" && !UUID_RE.test(state.issuerOrgId)) {
			errors.push(bundle.getText("organizations.new.issuer.error.partnerRequired") ?? "Pick the issuer partner");
		}
		if (draft.name.trim().length === 0) {
			errors.push(bundle.getText("organizations.new.error.nameRequired") ?? "Name is required");
		}
		if (!UUID_RE.test(state.parentOrgId)) {
			errors.push(bundle.getText("organizations.new.error.parentMissing") ?? "Could not resolve parent organization");
		}
		const cardCode = draft.cardCode.trim();
		if (cardCode.length > 0 && !CARD_CODE_PATTERN.test(cardCode)) {
			model.setProperty("/cardCodeValueState", "Error");
			errors.push(bundle.getText("organization.detail.cardCode.invalidFormat") ?? "CardCode invalid");
		}
		if (errors.length > 0) {
			MessageBox.warning(errors.join("\n"));
			return;
		}

		const fetcher = this.getOwnerComponent().getAuthFetch();
		try {
			const created = await fetcher.json<{ id: string } | string>("/api/v1/organizations", {
				method: "POST",
				body: JSON.stringify({
					parentOrganizationId: state.parentOrgId,
					type: state.type,
					name: draft.name.trim(),
					taxId: draft.taxId.trim().length > 0 ? draft.taxId.trim() : null
				})
			});
			const newId = typeof created === "string" ? created : created.id;
			if (!UUID_RE.test(newId)) {
				throw new Error("Backend did not return new organization id");
			}

			const hasProfile = [draft.country, draft.city, draft.region, draft.postalCode, draft.email,
				draft.contractDate, draft.contractExpiration, draft.customerStatus].some(v => v.trim().length > 0);
			if (hasProfile) {
				const trim = (v: string): string | null => { const t = v.trim(); return t.length > 0 ? t : null; };
				await fetcher.json(`/api/v1/organizations/${newId}/profile`, {
					method: "PUT",
					body: JSON.stringify({
						country: trim(draft.country), city: trim(draft.city), region: trim(draft.region),
						postalCode: trim(draft.postalCode), email: trim(draft.email),
						contractDate: trim(draft.contractDate), contractExpirationDate: trim(draft.contractExpiration),
						customerStatus: trim(draft.customerStatus)
					})
				});
			}

			const permissions = this.getOwnerComponent().getPermissions();
			if (cardCode.length > 0 && permissions.has("orgs.edit-integration")) {
				await fetcher.json(`/api/v1/organizations/${newId}/integration`, {
					method: "PUT",
					body: JSON.stringify({ cardCode })
				});
			}

			this.addOrgDialog?.close();
			const successKey = state.type === "Partner" ? "organizations.new.success.partner" : "organizations.new.success.customer";
			MessageToast.show(bundle.getText(successKey) ?? "Organization created");
			this.getOwnerComponent().getModel("odata")?.refresh();
			if (this.isPartnerUser()) {
				this.navToOrganization(newId);
			} else {
				const detailRoute = state.type === "Partner" ? "partnerDetail" : state.type === "EndCustomer" ? "customerDetail" : "organizationDetail";
				this.navTo(detailRoute, { id: newId });
			}
		} catch (e) {
			const err = e as ResponseError;
			// #8: the backend answers with the domain code "organization.name_already_taken" — show it
			// on the name field rather than as a raw code in a MessageBox. Matched on the `code`
			// member, so a code that merely appears inside some other message cannot trigger it.
			if (problemCode(err.bodyText) === "organization.name_already_taken") {
				const text = bundle.getText("organizations.new.error.nameTaken") ?? "Organizacja o tej nazwie już istnieje.";
				model.setProperty("/nameValueState", "Error");
				model.setProperty("/nameValueStateText", text);
				MessageBox.warning(text);
				return;
			}
			MessageBox.error(problemMessage(
				err.bodyText, bundle.getText("organizations.new.failure") ?? "Failed to create organization"));
		}
	}
}

/** Escape a single quote per OData v4 string literal rules: ' → '' */
function escapeODataString(value: string): string {
	return value.replace(/'/g, "''");
}

/** "partners-active" → "partnersActive" — żeby pasowało do i18n key konwencji. */
function variantKeyToI18n(key: string): string {
	return key.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
}
